import WebStorageService from '_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import { isNonReturnableRoute } from '@/utils/post-login-redirect'
import React from 'react'
import { createRoot } from 'react-dom/client'

import '@fontsource/caveat/latin.css'
import '@fontsource/dancing-script/latin.css'
import '@fontsource/economica/latin.css'
import '@fontsource/fira-sans/latin.css'
import '@fontsource/gruppo/latin.css'
import '@fontsource/kalam/latin.css'
import '@fontsource/merriweather-sans/latin.css'
import '@fontsource/open-sans-condensed/latin.css'
import '@fontsource/pt-sans-narrow/latin.css'
import '@fontsource/shadows-into-light/latin.css'
import '@fontsource/tulpen-one/latin.css'
import '@fontsource/voltaire/latin.css'
import '@fontsource/titillium-web/latin.css'
import '@fontsource/source-serif-pro/latin.css'
import '@fontsource/source-sans-pro/latin.css'

import { LicenseManager } from 'ag-grid-enterprise'
import common from '@/configs/common.js'
import RootComponent from '@/root-component'
import { init as initApm } from '@elastic/apm-rum'
import { apmConfig, apiRoot } from './environment'

import { pdfjs } from 'react-pdf'

// Suppress the benign ResizeObserver loop error the widget launcher can trigger.
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.stopImmediatePropagation()
  }
})

console.log('App is starting')

const originalConsoleError = console.error
console.error = (...args) => {
  if (
    args[0]
      && typeof args[0] === 'string'
      && (
        args[0].includes('defaultProps will be removed from function components')
        || args[0].includes('forwardRef render functions do not support propTypes or defaultProps')
        || args[0].includes('Invalid argument supplied to oneOfType. Expected an array of check functions')
      )
  ) {
    return
  }
  originalConsoleError(...args)
}

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.js', import.meta.url).toString()

const apmActiveOverride = WebStorageService.getItem(WebStorageKey.APM_ACTIVE)
const disableInstrumentationsOverride = WebStorageService.getItem(WebStorageKey.APM_DISABLED_INSTRUMENTATION)
const apmLogLevel = WebStorageService.getItem(WebStorageKey.APM_LOG_LEVEL)
const disableInstrumentations = disableInstrumentationsOverride !== null ? disableInstrumentationsOverride.split(',') : apmConfig.disableInstrumentations
const apmActive = apmActiveOverride !== null ? apmActiveOverride : apmConfig.active
const apm = initApm({
  ...apmConfig,
  active: apmActive,
  disableInstrumentations,
  logLevel: apmLogLevel || 'error',
  // Set service version (required for sourcemap feature)
  serverUrl: `${apiRoot}/api/rum`,
  serviceVersion: `${ENV.version}`,
  centralConfig: false,
  apmRequest({ xhr }) {
    if (orgamax?.user?.loggedIn && orgamax?.user?.token) {
      xhr.setRequestHeader('authorization', `Bearer ${orgamax.user.token}`)
      return true
    }
    return false
  },
})

if (apmActive) {
  console.log(`RUM Active with disable instrumentation [${disableInstrumentations.join(', ')}]`)
  const origError = console.error
  console.error = function(error) {
    try {
      if (error instanceof Error) {
        apm.captureError(error)
      } else {
        apm.captureError(new Error(JSON.stringify(error)))
      }
    } finally {
      origError.apply(console, arguments)
    }
  }
}

window.CelloAttribution = window.CelloAttribution || function(t, ...o) {
  if (t === 'getReferral') {
    throw new Error('getReferral is not supported in this context. Use getUcc instead.')
  }

  let resolve, reject
  // eslint-disable-next-line
  const promise = new Promise((res, rej) => {
    resolve = res
    reject = rej
  })

  window.CelloAttributionCmd = window.CelloAttributionCmd || []
  window.CelloAttributionCmd.push({
    command: t,
    args: o,
    resolve,
    reject,
  })

  return promise
}

const orgamax = require('ampersand-app')

const initEnvironmentVariables = require('init/environment_variables').default
initEnvironmentVariables(window)
require(`../generated/${orgamax.buildTarget}/iconfont/iconfont`)
// import main styles
require('@/modules/base/style/app')
require('@/modules/base/style/variables.css')
require('@/modules/base/style/variables')

const path = window.location.pathname
const queryString = window.location.search
if (!isNonReturnableRoute(path)) {
  WebStorageService.setItem(WebStorageKey.RETURN_AFTER_LOGIN, path + queryString)
}

/* eslint-disable */
console.log(`
 _____________________
< Welcome to ${ENV.product} ${ENV.version}! >
 ---------------------
        \\   ^__^
         \\  (oo)\_______
            (__)\       )\/\\
              ||--www |
              ||     ||
`)
/* eslint-enable */

// store globals
window.$ = window.jQuery = require('jquery')
window.orgamax = orgamax

const UserModel = require('@/modules/account/model/user_model').default
const { startPostHogTracking } = require('@/modules/posthog/posthog-tracking')
const polyfills = require('@/modules/base/util/polyfills').default
const Registry = require('@/modules/base/util/registry').default
const rootEl = document.getElementById('rootEl')

orgamax.extend({
  registry: new Registry(),
  layout: null,
  apm,
  init: function() {
    polyfills.init()
    // init user and use it in error logging
    this.user = new UserModel()
    this.user.initPendo()
    // resolves the environment and waits for the Usercentrics consent before the SDK is loaded
    startPostHogTracking()
    this.user.initPostHog()

    const root = createRoot(rootEl)
    root.render(<RootComponent user={this.user} />)
  },
})

// load jquery plugins
require('owl.carousel')
require('dotdotdot')
require('scrolltofixed')
require('webui-popover')
require('spectrum-colorpicker')
require('dragscroll')
const moment = require('moment')

moment.locale('de')
LicenseManager.setLicenseKey(common.agGridLicense.key)

// // init
// const interval = window.setInterval(() => {
//   window.clearInterval(interval)
// }, 50)

orgamax.init()
